import { formatInZone, racesOnPoints, valueOf } from '@fsp/domain';
// The frame every screen lives in (APP_SHELL.md, SITE_HOME.md): fixed top
// bar with the league switcher, rail on tablet and desktop, bottom tabs on
// phone, content region that scrolls internally. The browser never scrolls.
//
// Two modes from the route: league mode inside `/l/:leagueId/...`, hub mode
// everywhere else.
//
// Drawn through the kit (LOOKS.md Part 7): icons by meaning, menus and
// sheets from `kit/overlays`, layout from classes.

import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as PointerClick,
  type ReactNode,
} from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BRAND } from '../branding';
import { deviceZone, useHome, useHub, useReaderZone } from '../data/hooks';
import { avatarColor, useSession } from '../data/session';
import { useAccountPrefs } from '../data/accountPrefs';
import { useLeagueEntry } from '../data/leagueEntry';
import { useDetailContext } from '../data/detail';
import { DETAIL_LABEL, DETAIL_LEVELS, type DetailLevel, type LookId, type Theme } from '@fsp/theme';
import { useTheme } from '../theme/ThemeProvider';
import { useLookContext } from '../data/look';
import { LOOKS, lookOf } from '../looks/registry';
import { useSim } from '../data/SimProvider';
import { useLeagueSource } from '../data/leagueSource';
import { showLeagueChat, showSimClock } from './visibility';
import { leagueIdFromPath } from '../data/route';
import { useActiveLeague } from '../data/LeaguesProvider';
import { DEMO_PATH, isDemoLeague, useDemoCard } from '../data/demo';
import { LiveDot, TeamMark } from '../kit';
import { Icon, type IconName } from '../kit/Icon';
import { Hint, Menu, type MenuEntry } from '../kit/overlays';
import { Avatar, Button, Count, IconButton } from '../kit/ui';
import { getSport } from '../sports/registry';
import { ChampionMoment } from '../screens/shared/ChampionMoment';
import { SignUp } from '../demo/SignUp';

import { useTutorial } from '../demo/TutorialProvider';
import { ChatDrawer, useChatDrawer } from './chatDrawer';
import { AboutButton, AboutSheet } from './AboutButton';
import { Bell } from './Bell';
import { NotificationDrawer } from './notificationDrawer';
import { DemoLine } from './DemoLine';
import { ConnectionLine } from './ConnectionLine';
import { Moments } from './Moments';
import { isInstalled } from '../install/platform';
import { useEditTeam } from './editTeam';
import { useMyTeamHere } from '../data/team';
import { SimClock } from './SimClock';
import { useTier } from './useTier';
import { watchPhoneFlow } from '../kit/phoneFlow';
import { PullToRefresh } from '../kit/pullRefresh';
import { tabMoves, useScreenMotion } from './screenMotion';
import { refreshReads } from '../data/read';
import {
  expectTabReturn,
  forAccount,
  recallTab,
  rememberTab,
  scrollToTop,
  tabOf,
  useScrollMemory,
} from './tabMemory';
import { LEAGUE_TAB_PATHS } from './leagueScreens';
import { useViewedPeriod } from '../data/period';
import '../styles/shell.css';
import { RouteBoundary } from './ScreenBoundary';
import { openFinder } from '../kit/finder';
import { FindAnything } from './findAnything';
import { KeyboardMap } from './keyboardMap';
import { CompareTrayBar, CompareTrayProvider, useCompareTray } from './compareTray';
import { jumping, useWayBack } from './wayBack';
import { useMorphLanded } from './morph';
import { AppearanceSheet, openAppearance } from './appearanceSheet';
// A visit ends after the page has been away a while (B-411).
import './visits';
import '../styles/awards.css';

type NavItem = { to: string; label: string; icon: IconName; end?: boolean };
type RailEntry = NavItem;
/** A phone tab: a route, or a route that stands for a group. */
type TabItem = NavItem;

/**
 * MY TEAM, GAME DAY, PLAYERS, LEAGUE — the four every fantasy app leads with
 * (ESPN's Roster, Matchup, Players, League; Yahoo's Team, Matchup, Players,
 * League), and nothing under them (B-403): the league's own screens are the
 * League front page's sections, and every one is found by name (B-402). My
 * Team is the league's own address: the roster, not a dashboard (B-211).
 * Then CALENDAR, a place of its own (DIRECTION U30, B-568): when to be here.
 */
function leagueNav(base: string, setup: boolean, week = ''): RailEntry[] {
  return [
    { to: `${base}${week}`, label: 'My Team', icon: 'people' as const, end: true },
    ...(setup ? [{ to: `${base}/draft`, label: 'Draft', icon: 'draft' as const }] : []),
    { to: `${base}/matchup${week}`, label: 'Game Day', icon: 'matchup' as const },
    { to: `${base}/players`, label: 'Players', icon: 'search' as const },
    { to: `${base}/league`, label: 'League', icon: 'leagues' as const },
    { to: `${base}/calendar`, label: 'Calendar', icon: 'calendar' as const },
  ];
}

/**
 * THE WEEK TRAVELS (B-216): with another week on screen, My Team, Matchup and
 * the Scoreboard open on it too, so stepping back to week 2 and tapping
 * Matchup shows week 2's. `week` is `?period=N`, or '' for now.
 */
function leagueTabs(base: string, setup: boolean, week = ''): TabItem[] {
  return [
    { to: `${base}${week}`, label: 'My Team', icon: 'people' as const, end: true },
    setup
      ? { to: `${base}/draft`, label: 'Draft', icon: 'draft' as const }
      : { to: `${base}/matchup${week}`, label: 'Game Day', icon: 'matchup' as const },
    { to: `${base}/league`, label: 'League', icon: 'leagues' as const },
    { to: `${base}/players`, label: 'Players', icon: 'search' as const },
    // THE CALENDAR IN YOU'S PLACE (B-568): the account menu behind the avatar
    // already held every door You did — the account, achievements,
    // notifications, the leagues, installing, the demo, signing out — so a
    // phone keeps five tabs (DIRECTION U30).
    { to: `${base}/calendar`, label: 'Calendar', icon: 'calendar' as const },
  ];
}

/**
 * Without an account there is no account page — the Shell guard sends it back
 * to the front door — so offering it in the rail was a link that silently did
 * nothing.
 */
function hubNav(anonymous: boolean): NavItem[] {
  return [
    {
      to: anonymous ? '/demo' : '/',
      label: anonymous ? 'Chapters' : 'Leagues',
      icon: 'leagues' as const,
      end: true,
    },
    ...(anonymous ? [] : [{ to: '/account', label: 'Account', icon: 'person' as const }]),
    // NOT APPEARANCE: it is an account setting, reached from the account menu
    // like Notifications (B-214).
  ];
}

/**
 * A link that jumps between places (the rail, the brand): a jump in this
 * window only, not when a modified click opens it in another (B-398).
 */
function jumpHere(event: PointerClick): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
    return;
  jumping();
}

function nextDetail(level: DetailLevel): DetailLevel {
  const i = DETAIL_LEVELS.indexOf(level);
  return DETAIL_LEVELS[(i + 1) % DETAIL_LEVELS.length]!;
}

/** Mode's stops, in the Appearance screen's order. */
const MODES = ['dark', 'light', 'system'] as const satisfies readonly Theme['mode'][];
const MODE_LABEL: Record<Theme['mode'], string> = {
  dark: 'Dark',
  light: 'Light',
  system: 'System',
};

function nextMode(mode: Theme['mode']): Theme['mode'] {
  return MODES[(MODES.indexOf(mode) + 1) % MODES.length]!;
}

/** The next look round, as Detail steps through its levels (the list, never typed out). */
function nextLook(look: LookId): LookId {
  const i = LOOKS.findIndex((l) => l.id === look);
  return LOOKS[(i + 1) % LOOKS.length]!.id;
}

/** The frame, inside the compare tray it shows (B-375): the tray is the app's, not a screen's. */
export function AppShell({ children }: { children: ReactNode }) {
  return (
    <CompareTrayProvider>
      <Frame>{children}</Frame>
    </CompareTrayProvider>
  );
}

function Frame({ children }: { children: ReactNode }) {
  const tier = useTier();
  const { session } = useSession();
  const { activeLeagueId, state, tutorial } = useSim();
  const source = useLeagueSource();
  const location = useLocation();
  // THE REAL LEAGUE WINS WHEREVER THERE IS ONE. The chrome asked the
  // simulator three questions about the league on screen — its name, whether
  // it has drafted, whether I run it — and for a real league those are three
  // answers about somebody else's world. It was usually harmless because a
  // real id is usually absent from the simulated world, which left the shell
  // drawing hub chrome; where the ids collide, and `db:seed:local` makes them
  // collide because it generated the fixture FROM the simulator, the shell
  // drew Ken's practice league's title and Ken's commissioner buttons over
  // Robin's real league.
  //
  // A REAL LEAGUE THAT HAS DRAFTED DRAWS THE LEAGUE NAV TOO. `leagueMode`
  // came from the simulator alone until 2026-09-24, on the reasoning that a
  // real league had only its pre-draft page — which stopped being true when
  // E12 put its screens on the API. A seeded league shares an id with a
  // simulated one and so got the nav by accident; a league made through the
  // form (`lg-…`) has no simulated twin and got hub chrome on every screen,
  // with no way to its lineup, matchup or standings but Home's links (found
  // 2026-09-24, before Ken's first league drafted). A real league IN SETUP
  // still gets hub chrome, because every one of its routes but priorities
  // renders the pre-draft page, and a rail of tabs that all land on that page
  // would be worse. The tabs a real league has no server behind yet land on
  // the not-connected screen, which says so and names the ones that are real
  // (B-134).
  const real = useActiveLeague();
  const leagueMode = activeLeagueId !== null || (real !== null && real.league.status !== 'setup');
  const setup =
    real !== null ? real.league.status === 'setup' : leagueMode && state.status === 'setup';
  const base = `/l/${real?.league.id ?? activeLeagueId}`;
  const suffix = location.pathname.replace(/^\/l\/[^/]+/, '');
  const simClock = showSimClock(tutorial, leagueMode, source);
  // CHAT IS THERE BEFORE THE DRAFT TOO, though a real league in setup draws
  // hub chrome: the weeks before it are when a league is joining and has the
  // most to say (B-187), and the drawer needs nothing the setup lacks.
  // THE WEEK STEPPER, ON THE SCREENS THAT SHOW A WEEK (APP_SHELL.md, D4,
  // B-216): My Team and another team's page, my Matchup, and the Scoreboard.
  // It was on every league screen but eleven, as a label with arrows that did
  // nothing; a screen with no week of its own (the standings, the League
  // page) has nothing for it to move.
  const periodAware =
    leagueMode &&
    (suffix === '' ||
      suffix === '/' ||
      suffix.startsWith('/teams/') ||
      suffix === '/matchup' ||
      suffix === '/scoreboard');
  // The practice league keeps only the current week's lineup, and builds a
  // matchup's rows from it, so its team pages and its Matchup show the week
  // and do not step — a past week's header over this week's rows was the
  // review's finding (2026-09-27). Its Scoreboard is computed per week.
  const steps = periodAware && !(source === 'sim' && suffix !== '/scoreboard');
  const viewed = useViewedPeriod();
  const week = periodAware && viewed.period !== undefined ? `?period=${viewed.period}` : '';
  const hub = hubNav(!session);
  const nav = leagueMode ? leagueNav(base, setup, week) : hub;
  const chat = useChatDrawer();
  const tabs: TabItem[] = leagueMode ? leagueTabs(base, setup, week) : hub;
  // The League tab stands for the whole group: lit on the League page and on
  // every screen it opens, another team's page included.
  const leagueTabActive = leagueMode && LEAGUE_TAB_PATHS.some((p) => suffix.startsWith(p));
  // Compare is the Players tab's: picked there, and its Back goes there (B-407).
  const playersTabActive = leagueMode && suffix.startsWith('/compare');
  const litBy = (to: string, isActive: boolean) =>
    isActive ||
    (to === `${base}/league` && leagueTabActive) ||
    (to.startsWith(`${base}/players`) && playersTabActive);
  // ON A PHONE A SCREEN SCROLLS AS ONE (UX-38): its sticky row's height, for
  // the column names that stick under it.
  const content = useRef<HTMLElement>(null);
  useEffect(() => {
    if (tier !== 'phone' || content.current === null) return;
    return watchPhoneFlow(content.current);
  }, [tier]);
  // THE TABS KEEP YOUR PLACE (UX-39): each remembers its last screen and each
  // screen its scroll, as an app's tab bar does.
  forAccount(session?.userId ?? null);
  const here = location.pathname + location.search;
  const currentTab = leagueMode ? tabOf(suffix, LEAGUE_TAB_PATHS) : null;
  useEffect(() => {
    if (currentTab !== null) rememberTab(base, currentTab, here);
  }, [base, currentTab, here]);
  useScrollMemory(content, tier === 'phone');
  // SCREENS MOVE LIKE AN APP'S (B-313): deeper from the right, back from the
  // left, a tab switch still.
  useScreenMotion(content, tier === 'phone');
  // A MORPH LANDS WHEN THE NEW SCREEN IS DRAWN (`shell/morph.ts`): said here,
  // once for every screen, so no screen can leave one waiting.
  useMorphLanded();
  // NOTHING IS A ONE-WAY TRIP (B-398, UX-13): every screen below a place's
  // first has a Back of its own, named for where it goes.
  const back = useWayBack(setup);
  const navigate = useNavigate();
  const onTab = (event: PointerClick, to: string) => {
    // A modified click is the browser's: a new tab, a new window.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    const tab = leagueMode
      ? tabOf(to.replace(/^\/l\/[^/?]+/, '').replace(/\?.*$/, ''), LEAGUE_TAB_PATHS)
      : null;
    // The hub's tabs are plain links; they switch in place too.
    tabMoves('still');
    // A TAB IS A JUMP BETWEEN PLACES: Back after one goes up, not sideways.
    // Said only where it goes somewhere (B-398), never for a tap that only
    // scrolls, which would make the next link a jump.
    if (tab === null) {
      jumping();
      return;
    }
    event.preventDefault();
    const root = to.replace(/\?.*$/, '');
    if (tab !== currentTab) {
      // Where it was left. My Team and Matchup open on the week on screen
      // instead (B-216), which `to` already carries.
      const left = tab === 'league' || tab === 'players' ? recallTab(base, tab) : null;
      const target = left ?? to;
      expectTabReturn(target);
      jumping();
      navigate(target);
    } else if (location.pathname !== root) {
      // Deeper in the tab: back to its first screen, moving as Back does.
      tabMoves('back');
      jumping();
      navigate(to);
    } else {
      scrollToTop(content.current);
    }
  };

  const tray = useCompareTray();
  const trayShown = leagueMode && tray.shown && !location.pathname.endsWith('/compare');
  return (
    <div
      className="fsp-frame"
      data-tier={tier}
      data-mode={leagueMode ? 'league' : 'hub'}
      data-tray={trayShown || undefined}
    >
      <header className="fsp-topbar">
        <div className="fsp-topbar-left">
          {/* THE WAY BACK TAKES THE BRAND'S CORNER (B-398), where an app's
              navigation bar has its Back: the brand goes to the leagues,
              which the switcher's All leagues does too, and comes back on
              every place's first screen. */}
          {back ? (
            <BackButton label={back.label} onClick={back.go} short={tier === 'phone'} />
          ) : (
            <NavLink to="/" className="fsp-brand fsp-disp" onClick={jumpHere}>
              {BRAND.shortName}
            </NavLink>
          )}
          <LeagueSwitcher />
          {/* A phone's bar has no room for the week and the tour's clock
              together: "Week 3" was drawn over "3:30" (round 3's review,
              SCREENS-10). The clock is the tour's control, and the screen
              under it names its own week. */}
          {periodAware && !(tier === 'phone' && simClock) ? <PeriodStepper steps={steps} /> : null}
        </div>
        <div className="fsp-topbar-right">
          {simClock ? <SimClock compact={tier === 'phone'} /> : null}
          {/* FIND ANYTHING BY NAME (B-402), from a tablet up; a phone's is the
              account menu's Search. */}
          {tier !== 'phone' ? (
            <Hint label="Search ( / or Ctrl K )">
              <IconButton icon="search" label="Search" variant="quiet" onClick={openFinder} />
            </Hint>
          ) : null}
          {/* Which build this is, for everybody, always (O2, B-112): in the
              bar from a tablet up, and in the menu on a phone, whose bar has
              no room for it beside the week (B-276). */}
          {tier !== 'phone' ? <AboutButton /> : null}
          {/* THE BELL IS SOMEBODY'S (K4): a signed-out visitor and the tour
              have no record to count, so they have no bell. */}
          {session !== null && !tutorial ? <Bell /> : null}
          <AccountMenu />
        </div>
      </header>
      <ConnectionLine />
      <DemoLine />

      <div className="fsp-body">
        {tier !== 'phone' ? (
          <nav className="fsp-rail" aria-label="Primary">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `fsp-rail-item ${litBy(n.to, isActive) ? 'active' : ''}`
                }
                onClick={jumpHere}
              >
                <Icon name={n.icon} size="lg" />
                <span>{n.label}</span>
              </NavLink>
            ))}
            <div className="fsp-rail-spacer" />
            {showLeagueChat(leagueMode || setup, source) ? (
              <button
                className={`fsp-rail-item ${chat.open ? 'active' : ''}`}
                type="button"
                // Its name starts with its words (WCAG 2.5.3); the count is
                // said after them rather than read off the corner.
                aria-label={chat.unread ? `Chat, ${chat.unread} unread` : 'Chat'}
                onClick={chat.toggle}
              >
                <span className="fsp-rail-chat">
                  <Icon name="chat" size="lg" />
                </span>
                <span>Chat</span>
                {/* AFTER THE WORD IN THE PAGE, on the icon's corner on the
                    screen, with a space between in the page that the flex
                    layout does not draw: its words read "Chat 8", which its
                    name holds in order (WCAG 2.5.3); they read "8Chat" before
                    (the audit of 2026-10-11). */}
                {chat.unread ? ' ' : null}
                {chat.unread ? (
                  <span className="fsp-chat-badge fsp-rail-badge fsp-rail-badge-end" aria-hidden>
                    {chat.unread}
                  </span>
                ) : null}
              </button>
            ) : null}
          </nav>
        ) : null}
        <main className="fsp-content" ref={content}>
          {/* On a phone, pull down at the top to ask the screen again (B-312). */}
          {tier === 'phone' ? <PullToRefresh area={content} onRefresh={refreshReads} /> : null}
          <RouteBoundary>{children}</RouteBoundary>
        </main>
      </div>

      {/* THE PLAYERS PICKED TO COMPARE, between the screen and the tabs, so
          the screen makes room for them rather than hiding rows under them. */}
      {trayShown ? <CompareTrayBar /> : null}
      {tier === 'phone' && showLeagueChat(leagueMode || setup, source) ? (
        <button
          type="button"
          className="fsp-chat-chip"
          // The count on the chip is said as part of its name (WCAG 2.5.3).
          aria-label={chat.unread ? `League chat, ${chat.unread} unread` : 'League chat'}
          onClick={chat.toggle}
        >
          <Icon name="chat" />
          {chat.unread ? <span className="fsp-chat-badge">{chat.unread}</span> : null}
        </button>
      ) : null}
      <ChatDrawer />
      <NotificationDrawer />
      <AppearanceSheet />
      {/* `g` and a letter to go somewhere, `?` for the map (B-418). */}
      <KeyboardMap />
      <FindAnything
        leagueMode={leagueMode}
        setup={setup}
        // The practice league's commissioner is the simulator's say.
        commissioner={real !== null ? real.league.isCommissioner : state.isCommissioner}
        week={week}
      />
      {/* Each league's own champion: the simulator's for the tour, the one a
          real league's season recorded for a real league (useMyChampionship,
          PLAN-2026-09-26-season-end S4) — never one over the other. */}
      {leagueMode && !setup ? <ChampionMoment /> : null}
      {/* A FIND, AS IT HAPPENS (ACHIEVEMENTS.md §12), and a milestone the
          next time the site is open after its week settled (H7): the
          account's, so on every screen, one card at a time, and nobody's in
          the tour. */}
      {session !== null && !tutorial ? <Moments /> : null}
      {tier === 'phone' ? (
        <nav className={`fsp-tabs fsp-ui-cols-${tabs.length}`} aria-label="Primary">
          {tabs.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={(event) => onTab(event, n.to)}
              className={({ isActive }) => `fsp-tab ${litBy(n.to, isActive) ? 'active' : ''}`}
            >
              <Icon name={n.icon} size="lg" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
      ) : null}
    </div>
  );
}

/** Current period label with arrows; only shown on period-aware league screens. */
/**
 * The week on screen, and the way to another (APP_SHELL.md "Period stepper",
 * D4, B-216). The label opens every week the league plays, now's marked; a
 * tablet or desktop has arrows beside it too, which a phone's bar has no room
 * for. Off now's week, *Now* beside it (and *Back to now* heading the list,
 * a phone's only way) goes back. Where the
 * screen cannot show another week (a practice league's lineup) it is the
 * week alone, as it always was.
 */
function PeriodStepper({ steps }: { steps: boolean }) {
  const tier = useTier();
  const home = valueOf(useHome());
  const { period, shown, now, weeks, go } = useViewedPeriod();
  // Asked for another week before the schedule has said what it is called:
  // no label, rather than now's over another week's screen.
  if (steps && weeks.length === 0 && period !== undefined) return <div className="fsp-period" />;
  if (!steps || weeks.length === 0) {
    return (
      <div className="fsp-period">
        <span>{home?.period.label ?? ''}</span>
      </div>
    );
  }
  const at = weeks.findIndex((w) => w.index === shown);
  const label = weeks[at]?.label ?? home?.period.label ?? '';
  const prev = at > 0 ? weeks[at - 1]! : null;
  const next = at >= 0 && at < weeks.length - 1 ? weeks[at + 1]! : null;
  const away = shown !== null && now !== null && shown !== now;
  const arrows = tier !== 'phone';
  return (
    <div className="fsp-period" data-away={away || undefined}>
      {arrows ? (
        <IconButton
          icon="previous"
          label={prev ? `Show ${prev.label}` : 'No earlier week'}
          variant="quiet"
          size="sm"
          disabled={prev === null}
          onClick={() => prev && go(prev.index)}
        />
      ) : null}
      <Menu
        width={200}
        maxHeight={360}
        title="Pick a week"
        target={
          <button type="button" className="fsp-period-label" aria-label={`${label}. Pick a week`}>
            <span>{label}</span>
            <Icon name="expand" size="sm" />
          </button>
        }
        entries={[
          // Off now's week, the way back heads the list — on a phone the only
          // way, since its bar has no room for the *Now* button beside it.
          ...(away && now !== null
            ? [
                {
                  kind: 'item' as const,
                  key: 'now',
                  icon: 'when' as const,
                  label: 'Back to now',
                  onClick: () => go(null),
                },
                { kind: 'divider' as const },
              ]
            : []),
          ...weeks.map((w) => ({
            kind: 'item' as const,
            key: String(w.index),
            label: w.label,
            current: w.index === shown,
            trail:
              w.index === now ? (
                <span className="fsp-cap">Now</span>
              ) : w.kind === 'playoff' ? (
                <span className="fsp-cap">Playoffs</span>
              ) : undefined,
            onClick: () => go(w.index),
          })),
        ]}
      />
      {arrows ? (
        <IconButton
          icon="next"
          label={next ? `Show ${next.label}` : 'No later week'}
          variant="quiet"
          size="sm"
          disabled={next === null}
          onClick={() => next && go(next.index)}
        />
      ) : null}
      {away && arrows ? (
        <Button size="xs" variant="quiet" onClick={() => go(null)}>
          Now
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The league switcher (SITE_HOME.md): every league grouped by sport with a
 * live dot and a chore badge. Switching keeps the screen kind when the
 * target league has it; a league still in setup lands on its Home.
 */
function LeagueSwitcher() {
  const hub = useHub();
  const zone = useReaderZone() ?? deviceZone();
  const location = useLocation();
  const navigate = useNavigate();
  const tier = useTier();
  // MY TEAM IN THE LEAGUE ON SCREEN, for "Edit my team" (SCENARIOS L2): the one place in
  // the shell a league's own team is named, and so the door to its name and
  // colors from every screen of that league. Absent without a seat.
  const mine = useMyTeamHere();
  const editTeam = useEditTeam();
  const entry = useLeagueEntry();
  // THE ROUTE, NOT THE SIMULATOR. `useHub` merges the real leagues with the
  // example ones, so the id in the URL finds either — where asking the
  // simulator for `activeLeagueId` found only its own, and left the switcher
  // saying "My leagues" while somebody stood inside a league they had just
  // made. `LeaguePreDraftScreen` carried a comment about that, and its
  // workaround (the screen naming its own league) is worth keeping either
  // way: the top bar is not the only place that answers "where am I".
  const activeLeagueId = leagueIdFromPath(location.pathname);
  // THE DEMO LEAGUE IS ON NOBODY'S HUB (O6): on its pages it is listed here
  // from its own card, so the bar names where the visitor is.
  const demoCard = useDemoCard();
  const leagues =
    isDemoLeague(activeLeagueId) &&
    demoCard !== null &&
    !hub.leagues.some((l) => l.league.id === demoCard.league.id)
      ? [...hub.leagues, demoCard]
      : hub.leagues;
  // AN EARLIER SEASON is not on the hub's list, and still names the bar.
  const earlier = useActiveLeague();
  const active =
    leagues.find((l) => l.league.id === activeLeagueId) ??
    (earlier?.league.id === activeLeagueId ? earlier : null);
  const label = active ? active.league.name : tier === 'phone' ? 'Leagues' : 'My leagues';

  const go = (leagueId: string, status: string) => {
    const current = leagueIdFromPath(location.pathname);
    const suffix = current ? location.pathname.replace(/^\/l\/[^/]+/, '') : '';
    // A matchup id and a team id belong to one league; carried into another
    // they name nothing there.
    const keep =
      status === 'in-season' &&
      suffix &&
      !suffix.startsWith('/matchup/') &&
      !suffix.startsWith('/teams/') &&
      // A plan belongs to one league's draft, and a practice league has none.
      !suffix.startsWith('/priorities');
    // My Team carries too — it is in every league. From outside a league, or
    // from a screen the other league has no match for, it is that league's
    // own front: where this person chose it opens (B-247).
    // Another league is a jump: its Back goes up within it, not back here.
    jumping();
    navigate(
      keep
        ? `/l/${leagueId}${suffix}`
        : current && suffix === ''
          ? `/l/${leagueId}`
          : entry(leagueId, status === 'setup'),
    );
  };

  const groups = new Map<string, typeof leagues>();
  for (const l of leagues) {
    const g = groups.get(l.league.sport) ?? [];
    g.push(l);
    groups.set(l.league.sport, g);
  }

  const entries: MenuEntry[] = [];
  for (const [sport, leagues] of groups) {
    entries.push({ kind: 'label', label: getSport(sport as never).name });
    for (const l of leagues) {
      entries.push({
        kind: 'item',
        key: l.league.id,
        onClick: () => go(l.league.id, l.league.status),
        lead: l.myTeam ? <TeamMark team={l.myTeam} size={24} /> : <Icon name="leagues" />,
        trail: (
          <span className="fsp-switcher-marks">
            {l.live ? <LiveDot /> : null}
            {l.chores?.length ? <Count value={l.chores.length} tone="warn" /> : null}
          </span>
        ),
        current: l.league.id === activeLeagueId,
        label: (
          <span className="fsp-switcher-entry">
            <span className="fsp-switcher-name">{l.league.name}</span>
            <span className="fsp-sub">
              {l.league.status === 'setup'
                ? `Drafts ${shortWhen(l.league.draftAt, zone)}`
                : l.standing
                  ? racesOnPoints(l.league.format.id)
                    ? `${ordinal(l.standing.rank)} of ${l.standing.of}`
                    : `${l.myTeam!.record.wins}–${l.myTeam!.record.losses} · ${ordinal(l.standing.rank)} of ${l.standing.of}`
                  : ''}
            </span>
          </span>
        ),
      });
    }
  }
  if (mine) {
    entries.push({ kind: 'divider' });
    entries.push({
      kind: 'item',
      key: 'edit-team',
      icon: 'edit' as const,
      label: 'Edit my team',
      trail: <TeamMark team={mine.team} size={22} />,
      onClick: editTeam.open,
    });
  }
  entries.push(
    { kind: 'divider' },
    {
      kind: 'item',
      key: 'all',
      icon: 'leagues' as const,
      label: 'All leagues',
      onClick: () => navigate('/'),
    },
    {
      kind: 'item',
      key: 'new',
      icon: 'add' as const,
      label: 'Start a league',
      onClick: () => navigate('/leagues/new'),
    },
  );

  return (
    <Menu
      width={300}
      title="Leagues"
      entries={entries}
      target={
        <button
          type="button"
          className="fsp-switcher"
          // Its name starts with the words on it (WCAG 2.5.3): the league.
          aria-label={`${label}, switch league`}
          data-phone={tier === 'phone' || undefined}
        >
          <span className="fsp-switcher-label">{label}</span>
          <Icon name="expand" size="sm" />
        </button>
      }
    />
  );
}

function AccountMenu() {
  const navigate = useNavigate();
  const { session, signOut } = useSession();
  const tour = useTutorial();
  // A PHONE'S BAR HAS NO ROOM FOR THE BUILD'S INFO (B-276), so its menu
  // carries it, in a sheet over the screen: the same lines, for everybody,
  // always (B-112).
  const phone = useTier() === 'phone';
  const [about, setAbout] = useState(false);
  const aboutEntry: MenuEntry[] = phone
    ? [
        {
          kind: 'item',
          key: 'about',
          icon: 'info' as const,
          label: 'About this build',
          onClick: () => setAbout(true),
        },
      ]
    : [];
  const aboutSheet = phone ? <AboutSheet opened={about} onClose={() => setAbout(false)} /> : null;
  // SEARCH on a phone, whose bar has no room for the button beside the week
  // (B-402): anything by name, players among it inside a league.
  const findEntry: MenuEntry[] = phone
    ? [
        {
          kind: 'item',
          key: 'find',
          icon: 'search' as const,
          label: 'Search',
          onClick: openFinder,
        },
      ]
    : [];

  const name = session?.displayName ?? '';
  const { detail, setDetail } = useDetailContext();
  const { deviceOwn } = useAccountPrefs();
  const { look, setLook } = useLookContext();
  // THE LOOK, IN THE ACCOUNT MENU (P9), as a setting like any other. Beside
  // Detail and the same shape: a tap
  // moves to the next look and applies it at once, the menu stays open so the
  // change is seen, and the Appearance screen has the same choice with a
  // preview of each.
  const lookEntry: MenuEntry = {
    kind: 'item',
    key: 'look',
    icon: 'looks' as const,
    label: 'Look',
    keepOpen: true,
    onClick: () => setLook(nextLook(look)),
    trail: <span className="fsp-cap fsp-menu-detail">{lookOf(look).label}</span>,
  };
  // MODE, BESIDE THE LOOK (B-300), in the same shape: a tap moves to the next
  // and the menu stays open, so dark, light or the system's can be tried
  // without leaving the page. Under a look drawn in one mode the row says
  // which and cannot be pressed, since a tap would change nothing on screen;
  // the Appearance screen still sets the Mode that comes back (B-207).
  const { theme, setTheme } = useTheme();
  const fixedMode = lookOf(look).mode ?? null;
  const modeEntry: MenuEntry = {
    kind: 'item',
    key: 'mode',
    icon: fixedMode ?? theme.mode,
    label: 'Mode',
    keepOpen: true,
    disabled: fixedMode !== null,
    onClick: () => setTheme({ ...theme, mode: nextMode(theme.mode) }),
    trail: (
      <span className="fsp-cap fsp-menu-detail">
        {fixedMode === null ? MODE_LABEL[theme.mode] : `Always ${fixedMode}`}
      </span>
    ),
  };
  // No account: the ordinary signed-out chrome. A sign-up call to action and a
  // menu holding the ways back. No avatar, no name, nothing that implies an
  // identity the visitor does not have.
  if (!session)
    return (
      <span className="fsp-guest-chip">
        <SignUp size="xs" label="Sign up" />
        <Menu
          width={220}
          align="end"
          title="Menu"
          target={
            <button type="button" className="fsp-guest-more" aria-label="Site menu">
              <Icon name="more" size="sm" />
            </button>
          }
          entries={[
            ...findEntry,
            { kind: 'label', label: 'Looking around' },
            {
              kind: 'item',
              key: 'demo',
              icon: 'play' as const,
              label: 'Demo league',
              onClick: () => navigate(DEMO_PATH),
            },
            {
              kind: 'item',
              key: 'appearance',
              icon: 'looks' as const,
              label: 'Appearance',
              onClick: openAppearance,
            },
            // THE DETAIL LEVEL, FOR A VISITOR TOO (O6, B-273): the demo
            // league's stat lines are at Standard, and a visitor starts at
            // Simple like everybody. Kept on this device, as theirs would be.
            {
              kind: 'item',
              key: 'detail',
              icon: 'live' as const,
              label: 'Detail',
              keepOpen: true,
              onClick: () => setDetail(nextDetail(detail)),
              trail: <span className="fsp-cap fsp-menu-detail">{DETAIL_LABEL[detail]}</span>,
            },
            lookEntry,
            modeEntry,
            ...aboutEntry,
            // WHAT THE SITE KEEPS (B-417), from every menu.
            {
              kind: 'item',
              key: 'privacy',
              icon: 'locked' as const,
              label: 'Privacy',
              onClick: () => navigate('/privacy'),
            },
            // WHERE THE NUMBERS COME FROM (B-592), beside it.
            {
              kind: 'item',
              key: 'credits',
              icon: 'credits' as const,
              label: 'Credits',
              onClick: () => navigate('/credits'),
            },
            { kind: 'divider' },
            {
              kind: 'item',
              key: 'front',
              icon: 'signout' as const,
              label: 'Back to the front page',
              onClick: () => tour.exit(),
            },
          ]}
        />
        {aboutSheet}
      </span>
    );
  return (
    <>
      <Menu
        width={220}
        align="end"
        title={name}
        target={
          <Avatar
            name={name}
            color={avatarColor(session.avatarSeed)}
            face={session.avatarFace}
            label="Account"
          />
        }
        entries={[
          { kind: 'label', label: name },
          ...findEntry,
          {
            kind: 'item',
            key: 'account',
            icon: 'person' as const,
            label: 'Account',
            onClick: () => navigate('/account'),
          },
          // ACHIEVEMENTS.md §11: straight to the wall.
          {
            kind: 'item',
            key: 'achievements',
            icon: 'achievements' as const,
            label: 'Achievements',
            onClick: () => navigate('/account/achievements'),
          },
          {
            kind: 'item',
            key: 'appearance',
            icon: 'looks' as const,
            label: 'Appearance',
            onClick: openAppearance,
          },
          {
            kind: 'item',
            key: 'notifications',
            icon: 'notifications' as const,
            label: 'Notifications',
            onClick: () => navigate('/account/notifications'),
          },
          // INSTALL.md §4.3: a quiet, permanent way in, never in the installed app.
          ...(isInstalled()
            ? []
            : [
                {
                  kind: 'item' as const,
                  key: 'install',
                  icon: 'install' as const,
                  label: 'Install the app',
                  onClick: () => navigate('/account/install'),
                },
              ]),
          {
            kind: 'item',
            key: 'leagues',
            icon: 'leagues' as const,
            label: 'My leagues',
            onClick: () => navigate('/'),
          },
          {
            kind: 'item',
            key: 'demo',
            icon: 'play' as const,
            label: 'Demo league',
            onClick: () => navigate(DEMO_PATH),
          },
          {
            kind: 'item',
            key: 'detail',
            icon: 'live' as const,
            label: 'Detail',
            keepOpen: true,
            onClick: () => setDetail(nextDetail(detail)),
            // This device's own level, when it keeps one (M2, PLAN-2026-10-02
            // §3): the switch changes what this device shows either way.
            trail: (
              <span className="fsp-cap fsp-menu-detail">
                {DETAIL_LABEL[detail]}
                {deviceOwn ? ' · this device' : ''}
              </span>
            ),
          },
          lookEntry,
          modeEntry,
          ...aboutEntry,
          // WHAT THE SITE KEEPS (B-417), from every menu.
          {
            kind: 'item',
            key: 'privacy',
            icon: 'locked' as const,
            label: 'Privacy',
            onClick: () => navigate('/privacy'),
          },
          // WHERE THE NUMBERS COME FROM (B-592), beside it.
          {
            kind: 'item',
            key: 'credits',
            icon: 'credits' as const,
            label: 'Credits',
            onClick: () => navigate('/credits'),
          },
          { kind: 'divider' },
          {
            kind: 'item',
            key: 'signout',
            icon: 'signout' as const,
            label: 'Sign out',
            onClick: () => {
              signOut();
              navigate('/');
            },
          },
        ]}
      />
      {aboutSheet}
    </>
  );
}

/**
 * The top bar's Back (B-398): an arrow and the name of where it goes, as an
 * app's navigation bar has. On a phone a long name (a team's) is just "Back",
 * so the league's name keeps its room; never cut off (UX-11), and what it is
 * called always says where it goes.
 */
export function BackButton({
  label,
  onClick,
  short,
}: {
  label: string;
  onClick: () => void;
  short: boolean;
}) {
  const brief = short && label.length > 10;
  return (
    <button type="button" className="fsp-back" onClick={onClick}>
      <Icon name="back" size="sm" />
      {brief ? (
        <>
          <span className="fsp-back-label" aria-hidden>
            Back
          </span>
          <span className="fsp-sr">Back to {label}</span>
        </>
      ) : (
        <>
          {/* A space the flex layout does not draw, so its name reads
              "Back to League" rather than "Back toLeague". */}
          <span className="fsp-sr">Back to</span> <span className="fsp-back-label">{label}</span>
        </>
      )}
    </button>
  );
}

/** A draft's time in the menu, in the reader's zone and named, as on the hub's card (B-180, B-181). */
function shortWhen(iso: string | null, zone: string): string {
  if (!iso) return 'soon';
  return formatInZone(iso, zone, { weekday: 'short', hour: 'numeric' });
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}
